import { Box, token } from '@terra/ui-styled-system';
import type { FindingSeverityType } from '@terra/shared-schemas-orchestrator';
import { MetaDefault } from './typography';

export type Severity = FindingSeverityType;

export type SeverityIndicatorSize = 'sm' | 'md';

export type SeverityIndicatorVariant = 'filled' | 'outline';

export type SeverityIndicatorProps = {
  severity: Severity;
  size?: SeverityIndicatorSize;
  variant?: SeverityIndicatorVariant;
};

const SEVERITY_STYLES: Record<
  Severity,
  { bg: string; border: string; fg: string }
> = {
  Critical: {
    bg: token('colors.bg.severity.critical'),
    border: token('colors.border.severity.critical'),
    fg: token('colors.fg.severity.critical'),
  },
  High: {
    bg: token('colors.bg.severity.high'),
    border: token('colors.border.severity.high'),
    fg: token('colors.fg.severity.high'),
  },
  Medium: {
    bg: token('colors.bg.severity.medium'),
    border: token('colors.border.severity.medium'),
    fg: token('colors.fg.severity.medium'),
  },
  Low: {
    bg: token('colors.bg.severity.low'),
    border: token('colors.border.severity.low'),
    fg: token('colors.fg.severity.low'),
  },
  Info: {
    bg: token('colors.bg.neutral.subtleSoft'),
    border: token('colors.border.neutral.subtle'),
    fg: token('colors.fg.muted'),
  },
};

const getSeverityLetter = (severity: Severity): string => {
  return severity.charAt(0).toUpperCase();
};

export function getSeverityFgColor(severity: FindingSeverityType): string {
  return (SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.Info).fg;
}

export const SeverityIndicator = ({
  severity,
  size = 'md',
  variant = 'filled',
}: SeverityIndicatorProps) => {
  const styles = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.Info;
  const isSmall = size === 'sm';
  const letter = getSeverityLetter(severity);
  const isOutline = variant === 'outline';

  if (isSmall) {
    const stroke = isOutline ? styles.fg : styles.border;
    const fill = isOutline ? 'transparent' : styles.bg;

    return (
      <Box
        as="span"
        width="16px"
        height="16px"
        flexShrink={0}
        display="inline-flex"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle
            cx="8"
            cy="8"
            r="7.5"
            fill={fill}
            stroke={stroke}
            strokeWidth="1"
          />
          <text
            x="8"
            y="8"
            textAnchor="middle"
            dominantBaseline="central"
            fill={styles.fg}
            fontSize="9"
            fontWeight="600"
          >
            {letter}
          </text>
        </svg>
      </Box>
    );
  }

  return (
    <Box
      width="20px"
      height="20px"
      flexShrink={0}
      borderRadius="full"
      borderWidth="1px"
      borderStyle="solid"
      boxSizing="border-box"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{
        borderColor: isOutline ? styles.fg : styles.border,
        backgroundColor: isOutline ? 'transparent' : styles.bg,
      }}
    >
      <MetaDefault
        fontSize="xs"
        fontWeight="medium"
        lineHeight="1"
        style={{ color: styles.fg }}
      >
        {letter}
      </MetaDefault>
    </Box>
  );
};
